import { Injectable, inject } from '@angular/core';
import { selectInterfaceLanguage } from '@mkp/core/state';
import { appHeaderActions } from '@mkp/core/state/actions';
import {
  EducationLevelService,
  EmploymentPositionService,
  EmploymentTypeService,
  LanguageLevelService,
  MetadataDictionary,
  MetadataLanguageService,
  MetadataResource,
  MetadataType,
} from '@mkp/metadata/data-access';
import { MetadataActions } from '@mkp/metadata/state/actions';
import { MessageError, isMessageError } from '@mkp/shared/data-access';
import { HIGH_LIMIT, LanguageIso } from '@mkp/shared/models';
import { objectKeys } from '@mkp/shared/util-format';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { combineLatest, forkJoin, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { emptyMetadataDictionary } from './metadata.reducer';
import { selectMetadatasLoaded } from './metadata.selectors';

@Injectable()
export class MetadataEffects {
  private readonly store = inject(Store);
  private actions$ = inject(Actions);
  private readonly services: Record<MetadataType, MetadataResource> = {
    language: inject(MetadataLanguageService),
    languageLvl: inject(LanguageLevelService),
    educationLvl: inject(EducationLevelService),
    seniorityLvl: inject(EmploymentPositionService),
    contractType: inject(EmploymentTypeService),
  };

  getMetadatasList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MetadataActions.getMetadatasList, appHeaderActions.switchAppLanguage),
        switchMap(() =>
          combineLatest([
            this.store.pipe(select(selectMetadatasLoaded)),
            this.store.pipe(select(selectInterfaceLanguage)),
          ])
        ),
        filter(isLanguageDefined),
        switchMap(([_, language]) => forkJoin(this.getMetadatasFromServices(language))),
        map(
          (metadatas) =>
            metadatas.reduce((acc, cur) => ({ ...acc, ...cur }), {}) as MetadataDictionary
        ),
        map((metadataDictionary) =>
          MetadataActions.getMetadatasListSuccess({ payload: metadataDictionary })
        ),
        catchError((unknownError: unknown) => {
          const error = isMetadatasError(unknownError)
            ? unknownError
            : { message: 'unknown metadata error' };
          return of(MetadataActions.getMetadatasListFailed({ error }));
        })
      ),
    { useEffectsErrorHandler: true }
  );

  private getMetadatasFromServices(language: LanguageIso) {
    return objectKeys(emptyMetadataDictionary).map((metadataType) =>
      this.services[metadataType].list({ offset: 0, rows: HIGH_LIMIT, language }).pipe(
        map((metadatas) => ({ [metadataType]: metadatas }) as const),
        take(1)
      )
    );
  }
}

const isMetadatasError = (error: unknown): error is MessageError =>
  isMessageError(error, 'MetadataEffects');
const isLanguageDefined = (
  array: [boolean, LanguageIso | undefined]
): array is [boolean, LanguageIso] => !array[0] && !!array[1];
